/**
 * FornadaFormDialog Component
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { fornadaSchema, type FornadaFormData } from "@/schemas/fornadaSchema";
import { fornadasAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { FornadaItensManager } from "./FornadaItensManager";
import { FornadaCustosAdicionaisManager } from "./FornadaCustosAdicionaisManager";
import type { FornadaCompleta, FornadaItem, FornadaCustoAdicional } from "@/types/fornada";

interface FornadaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  fornada: FornadaCompleta | null;
  onSuccess: () => void;
}

export function FornadaFormDialog({ open, onOpenChange, mode, fornada, onSuccess }: FornadaFormDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itens, setItens] = useState<FornadaItem[]>([]);
  const [custosAdicionais, setCustosAdicionais] = useState<FornadaCustoAdicional[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FornadaFormData>({
    resolver: zodResolver(fornadaSchema),
    defaultValues: {
      data_fornada: new Date().toISOString().split('T')[0],
      debitar_estoque: true,
      itens: [],
    },
  });

  useEffect(() => {
    if (open && fornada && mode === "edit") {
      reset({
        data_fornada: fornada.fornada.data_fornada,
        responsavel: fornada.fornada.responsavel,
        destino: fornada.fornada.destino,
        observacoes_gerais: fornada.fornada.observacoes_gerais,
        rendimento_total: fornada.fornada.rendimento_total,
        tempo_total_minutos: fornada.fornada.tempo_total_minutos,
        valor_hora_profissional: fornada.fornada.valor_hora_profissional,
        custo_mao_obra: fornada.fornada.custo_mao_obra,
        custo_gas: fornada.fornada.custo_gas,
        tipo_gas: fornada.fornada.tipo_gas,
        outras_despesas: fornada.fornada.outras_despesas,
        debitar_estoque: fornada.fornada.debitar_estoque,
        itens: fornada.itens,
        custos_adicionais: fornada.custos_adicionais || [],
      });
      setItens(fornada.itens);
      setCustosAdicionais(fornada.custos_adicionais || []);
    } else if (open && mode === "create") {
      reset({
        data_fornada: new Date().toISOString().split('T')[0],
        debitar_estoque: true,
        itens: [],
        custos_adicionais: [],
      });
      setItens([]);
      setCustosAdicionais([]);
    }
  }, [open, fornada, mode, reset]);

  // Sincronizar estado itens com react-hook-form
  useEffect(() => {
    setValue("itens", itens);
  }, [itens, setValue]);

  // Sincronizar custos adicionais
  useEffect(() => {
    setValue("custos_adicionais", custosAdicionais);
  }, [custosAdicionais, setValue]);

  // Calcular custos
  const custoReceitas = itens.reduce((acc, item) => acc + item.custo_total_item, 0);
  const custoItensAvulsos = custosAdicionais.reduce((acc, custo) => acc + custo.valor_total, 0);
  const custoMaoObra = watch("custo_mao_obra") || 0;
  const custoGas = watch("custo_gas") || 0;
  const outrasDespesas = watch("outras_despesas") || 0;
  
  const custoTotal = custoReceitas + custoMaoObra + custoGas + custoItensAvulsos + outrasDespesas;
  
  const rendimentoSugestao = itens.reduce((acc, item) => acc + (item.receita_rendimento || 0), 0);
  const rendimentoAtual = watch("rendimento_total");
  const custoMedioUnitario = rendimentoAtual && rendimentoAtual > 0 ? custoTotal / rendimentoAtual : 0;

  const onSubmit = async (data: FornadaFormData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        itens,
        custos_adicionais: custosAdicionais,
        custo_total: custoTotal,
        custo_medio_unitario: custoMedioUnitario,
      };

      if (mode === "create") {
        await fornadasAdapter.create(payload);
      } else if (fornada) {
        await fornadasAdapter.update(fornada.fornada.id_fornada, payload);
      }

      onSuccess();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar fornada",
        description: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nova Fornada" : "Editar Fornada"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold">Cabeçalho</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="data_fornada">Data da Fornada *</Label>
                <Input type="date" {...register("data_fornada")} />
                {errors.data_fornada && <p className="text-sm text-destructive">{errors.data_fornada.message}</p>}
              </div>
              <div>
                <Label htmlFor="responsavel">Responsável</Label>
                <Input {...register("responsavel")} placeholder="Nome do responsável" />
              </div>
              <div>
                <Label htmlFor="destino">Destino</Label>
                <Select value={watch("destino") || ""} onValueChange={(v) => setValue("destino", v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vitrine">Vitrine</SelectItem>
                    <SelectItem value="encomenda">Encomenda</SelectItem>
                    <SelectItem value="teste">Teste</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="observacoes_gerais">Observações Gerais</Label>
              <Textarea {...register("observacoes_gerais")} placeholder="Observações..." rows={3} />
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-semibold">Receitas da Fornada</h3>
            <FornadaItensManager itens={itens} onItensChange={setItens} />
            {errors.itens && (
              <p className="text-sm text-destructive mt-2">
                {errors.itens.message}
              </p>
            )}
          </div>

          <Separator />

          <div className="space-y-4">
            <FornadaCustosAdicionaisManager custos={custosAdicionais} onCustosChange={setCustosAdicionais} />
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-semibold">Custos Operacionais</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="valor_hora_profissional">Valor/Hora Profissional (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  {...register("valor_hora_profissional", { valueAsNumber: true })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="custo_mao_obra">Custo Mão de Obra (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  {...register("custo_mao_obra", { valueAsNumber: true })}
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Preencha manualmente ou calcule: tempo × valor/hora
                </p>
              </div>
              <div>
                <Label htmlFor="custo_gas">Custo Gás (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  {...register("custo_gas", { valueAsNumber: true })}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tipo_gas">Tipo de Gás</Label>
                <Select value={watch("tipo_gas") || ""} onValueChange={(v) => setValue("tipo_gas", v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GLP">GLP (Gás Liquefeito de Petróleo)</SelectItem>
                    <SelectItem value="GNV">GNV (Gás Natural Veicular)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="outras_despesas">Outras Despesas (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  {...register("outras_despesas", { valueAsNumber: true })}
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Energia, água, outras despesas gerais
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-semibold">Rendimento e Resumo</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rendimento_total">Rendimento Total *</Label>
                <Input
                  type="number"
                  step="0.001"
                  {...register("rendimento_total", { valueAsNumber: true })}
                  placeholder="Quantidade produzida"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  💡 Sugestão: {rendimentoSugestao.toFixed(0)} unidades
                </p>
              </div>
              <div>
                <Label htmlFor="tempo_total_minutos">Tempo Total (minutos)</Label>
                <Input
                  type="number"
                  {...register("tempo_total_minutos", { valueAsNumber: true })}
                  placeholder="Tempo gasto"
                />
              </div>
            </div>

            <div className="bg-muted p-4 rounded-md space-y-2">
              <div className="flex justify-between text-sm">
                <span>Custo Receitas:</span>
                <span>R$ {custoReceitas.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Custo Mão de Obra:</span>
                <span>R$ {custoMaoObra.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Custo Gás:</span>
                <span>R$ {custoGas.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Itens Avulsos:</span>
                <span>R$ {custoItensAvulsos.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Outras Despesas:</span>
                <span>R$ {outrasDespesas.toFixed(2)}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="font-medium">Custo Total:</span>
                <span className="font-bold text-lg">R$ {custoTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Custo Médio por Unidade:</span>
                <span className="text-sm font-semibold">R$ {custoMedioUnitario.toFixed(2)}</span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="debitar_estoque"
                checked={watch("debitar_estoque")}
                onCheckedChange={(checked) => setValue("debitar_estoque", !!checked)}
              />
              <Label htmlFor="debitar_estoque" className="text-sm font-normal">
                Debitar do estoque ao finalizar
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : mode === "create" ? "Criar Fornada" : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
